import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAssignKioskStore } from '@/features/assign-kiosk/stores/assign-kiosk-store';
import { useKioskSettingsStore } from '@/features/general-settings/stores/kiosk-settings-store';
import { useKioskPreviewStore } from '@/features/general-settings/stores/kiosk-preview-store';
import { useOperatorStore } from '@/core/stores/operator-store';
import { CustomizeSection } from '@/features/general-settings/components/sections/kiosk/CustomizeSection';
import { GeneralSection } from '@/features/general-settings/components/sections/kiosk/GeneralSection';
import { LanguagesSection } from '@/features/general-settings/components/sections/kiosk/LanguagesSection';
import { ScreensSection } from '@/features/general-settings/components/sections/kiosk/ScreensSection';
import { PaymentSection } from '@/features/general-settings/components/sections/kiosk/PaymentSection';
import { KioskPreviewPanel } from '@/features/general-settings/components/KioskPreviewPanel';
import { Button } from '@/core/components/ui/button';
import { Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import { validatePreAuth } from '@/features/general-settings/utils/pre-auth-validation';

export function KioskSettingsPanel() {
  const { t } = useTranslation('assignEv');
  const { kioskDetails, machineId: storeMachineId, locations, defaultCredit, loadDefaultCredit } = useAssignKioskStore();
  const store = useKioskSettingsStore();
  const { formFactor, setFormFactor } = useKioskPreviewStore();
  const selectedOperatorId = useOperatorStore((s) => s.selectedOperator?.operatorId);
  const [openSection, setOpenSection] = useState<string>('customize');
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const assignedLocation = locations.find((l) => l.id === kioskDetails?.locationId);
  const resolvedCountryCode = kioskDetails?.countryCode ?? assignedLocation?.countryCode ?? '';
  const resolvedPartyId = kioskDetails?.partyId ?? assignedLocation?.partyId ?? '';
  const canSave = !!kioskDetails?.locationId;

  useEffect(() => {
    const operatorId = kioskDetails?.operator ?? selectedOperatorId ?? 0;
    store.initFromJson(kioskDetails?.settings ?? null, operatorId, kioskDetails?.kioskSettings ?? null, kioskDetails?.operatorSettings ?? null);
    store.fetchLanguages().catch(() => {});
  }, [kioskDetails?.settings, kioskDetails?.operator, selectedOperatorId]);

  // Live Lynx Default Credit — ceiling + currency for the Pre-Auth presets
  useEffect(() => {
    if (storeMachineId) loadDefaultCredit(storeMachineId);
  }, [storeMachineId, loadDefaultCredit]);

  useEffect(() => {
    if (store.error) {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
      errorTimerRef.current = setTimeout(() => store.clearError(), 10000);
    }
  }, [store.error]);

  const handleSectionToggle = (sectionId: string) => {
    setOpenSection(openSection === sectionId ? '' : sectionId);
  };

  const isLoading = store.isLoading || store.isSaving;

  const authRatioPercent = store.settings?.authApprovalMinRatio != null
    ? store.settings.authApprovalMinRatio * 100
    : null;
  const acValue = store.settings?.acCreditBuffer ?? null;
  const dcValue = store.settings?.dcCreditBuffer ?? null;
  const minCostValue = store.settings?.minCostPerMinuteThreshold ?? null;
  const kwhCostValue = store.settings?.averageKwhCost ?? null;
  const hasValidationError =
    (authRatioPercent !== null && (authRatioPercent < 0 || authRatioPercent > 100)) ||
    (acValue !== null && (acValue < 0 || acValue > 99.9)) ||
    (dcValue !== null && (dcValue < 0 || dcValue > 99.9)) ||
    (minCostValue !== null && minCostValue <= 0) ||
    (kwhCostValue !== null && (kwhCostValue < 0.1 || kwhCostValue > 99999)) ||
    validatePreAuth(store.settings?.preAuth, defaultCredit?.amount).hasError;

  if (!store.settings) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {!kioskDetails && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>This machine has no kiosk record yet. Assign a location first to enable saving.</AlertDescription>
        </Alert>
      )}

<div className="fixed bottom-0 left-0 right-0 z-50 flex items-end justify-between px-4 py-3 pointer-events-none">
        <div className="pointer-events-auto">
          {store.error && (
            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 shadow-md opacity-90">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{store.error}</span>
            </div>
          )}
        </div>
        <div className="pointer-events-auto flex items-center gap-3">
          {hasValidationError && (
            <span className="text-xs text-red-600 font-medium">Fix validation errors to save</span>
          )}
          <Button
            variant="outline"
            onClick={() => store.resetSettings()}
            disabled={isLoading || !store.hasChanges()}
            className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Reset
          </Button>
          <Button
            onClick={async () => {
              if (!kioskDetails) return;
              const ok = await store.saveAsKioskSettings(
                storeMachineId ?? '',
                resolvedCountryCode,
                resolvedPartyId,
                kioskDetails.locationId ?? '',
                kioskDetails.settings ?? null
              );
              if (ok) toast.success(t('kiosk.saveSuccess'));
            }}
            disabled={isLoading || !store.hasChanges() || !canSave || hasValidationError}
            className="flex items-center gap-2 rounded-lg bg-yellow-400 px-6 py-2.5 text-sm font-bold text-gray-900 shadow-lg hover:bg-yellow-500 disabled:opacity-50"
          >
            {store.isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {store.isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {store.settings && (
        <>

          <div className="grid grid-cols-1 lg:grid-cols-[45%_55%] xl:grid-cols-[42%_58%] gap-6 h-[calc(100vh-340px)]">
            {/* Left Pane - Scrollable Settings Sections */}
            <div className="overflow-y-auto scrollbar-none space-y-4 bg-gray-100 rounded-lg p-4">
              <CustomizeSection
                isOpen={openSection === 'customize'}
                onToggle={() => handleSectionToggle('customize')}
              />
              <GeneralSection
                isOpen={openSection === 'general'}
                onToggle={() => handleSectionToggle('general')}
                hideGracePeriodSource
                hideConnectedStatus
              />
              <LanguagesSection
                isOpen={openSection === 'languages'}
                onToggle={() => handleSectionToggle('languages')}
              />
              <ScreensSection
                isOpen={openSection === 'screens'}
                onToggle={() => handleSectionToggle('screens')}
                hideTariffDisplay
                defaultCredit={defaultCredit}
              />
              <PaymentSection
                isOpen={openSection === 'payment'}
                onToggle={() => handleSectionToggle('payment')}
                hideMaxTransactionFee
              />
            </div>

            {/* Right Pane - Sticky Preview */}
            <div className="sticky top-0 h-full flex flex-row gap-2">
              {/* Form Factor Toggle - vertical on the left */}
              <div className="flex flex-col justify-start gap-2 pt-4">
                <button
                  className={`rounded px-3 py-2 text-sm font-medium transition-colors ${
                    formFactor === '4inch'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                  onClick={() => setFormFactor('4inch')}
                >
                  4"
                </button>
                <button
                  className={`rounded px-3 py-2 text-sm font-medium transition-colors ${
                    formFactor === '5inch'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                  }`}
                  onClick={() => setFormFactor('5inch')}
                >
                  5"
                </button>
              </div>
              <KioskPreviewPanel />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
